/**
 * Base64 for typed arrays, without depending on the browser or on Node.
 *
 * The engine has to run in both, so `btoa` and `Buffer` are both off-limits here.
 * A hand-rolled codec is ~40 lines and removes the branch entirely.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const LOOKUP = /*#__PURE__*/ (() => {
  const table = new Uint8Array(256).fill(255);
  for (let i = 0; i < ALPHABET.length; i++) table[ALPHABET.charCodeAt(i)] = i;
  return table;
})();

export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + ALPHABET[(n >>> 6) & 63]! + ALPHABET[n & 63]!;
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const n = bytes[i]! << 16;
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + '==';
  } else if (remaining === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + ALPHABET[(n >>> 6) & 63]! + '=';
  }
  return out;
}

export function base64ToBytes(text: string): Uint8Array {
  let clean = text;
  while (clean.endsWith('=')) clean = clean.slice(0, -1);

  const outLength = Math.floor((clean.length * 3) / 4);
  const out = new Uint8Array(outLength);

  let acc = 0;
  let bits = 0;
  let o = 0;
  for (let i = 0; i < clean.length; i++) {
    const v = LOOKUP[clean.charCodeAt(i)]!;
    if (v === 255) throw new Error(`Corrupt base64 at position ${i}: "${clean[i]}"`);
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >>> bits) & 0xff;
    }
  }
  return out;
}

/** Encode any typed array by reinterpreting its buffer as bytes. */
export function typedArrayToBase64(array: ArrayBufferView): string {
  return bytesToBase64(new Uint8Array(array.buffer, array.byteOffset, array.byteLength));
}

/** Decode into a fresh, correctly-aligned typed array of the given kind. */
export function base64ToTypedArray<T extends Uint8Array | Int16Array | Int32Array>(
  text: string,
  Kind: new (buffer: ArrayBufferLike) => T,
  expectedLength: number,
): T {
  const bytes = base64ToBytes(text);
  // Copy into a fresh buffer: the decoded bytes may not be aligned for Int16/Int32.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const array = new Kind(buffer);
  if (array.length !== expectedLength) {
    throw new Error(`Save contains a plane of length ${array.length}, expected ${expectedLength}`);
  }
  return array;
}
